import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api';
import { Dialog } from '../components/Dialog';
import { Disclosure } from '../components/Disclosure';
import { formatDateTime } from '../format';
import type {
  EditorialLesson,
  LessonCategory,
  LessonKind,
  LessonStatus,
} from '../../shared/editorial';

interface LessonDraft {
  category: LessonCategory;
  kind: LessonKind;
  text: string;
  context: string;
  sourceEvidenceText: string;
  status: LessonStatus;
}

type PreferenceView = 'rule' | 'preference' | 'example';

const EMPTY_LESSON: LessonDraft = {
  category: 'writing',
  kind: 'rule',
  text: '',
  context: '',
  sourceEvidenceText: '[]',
  status: 'active',
};

const VIEW_LABELS: Record<PreferenceView, string> = {
  rule: 'Rules',
  preference: 'Contextual preferences',
  example: 'Examples',
};

function toDraft(lesson: EditorialLesson): LessonDraft {
  return {
    category: lesson.category,
    kind: lesson.kind,
    text: lesson.text,
    context: lesson.context ?? '',
    sourceEvidenceText: JSON.stringify(lesson.sourceEvidence, null, 2),
    status: lesson.status,
  };
}

function evidence(value: string): Record<string, unknown>[] {
  const parsed: unknown = value.trim() ? JSON.parse(value) : [];
  if (!Array.isArray(parsed) || parsed.some((item) => !item || typeof item !== 'object' || Array.isArray(item))) {
    throw new Error('Evidence must be a JSON array of objects.');
  }
  return parsed as Record<string, unknown>[];
}

function evidenceLabel(item: Record<string, unknown>, index: number): string {
  for (const key of ['summary', 'title', 'text', 'note', 'source']) {
    const value = item[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  const sourceType = typeof item.sourceType === 'string' ? item.sourceType : 'Evidence';
  const sourceId = typeof item.sourceId === 'number' || typeof item.sourceId === 'string' ? ` #${item.sourceId}` : '';
  return `${sourceType}${sourceId || ` ${index + 1}`}`;
}

function LessonEditor({
  draft,
  setDraft,
  saving,
  onSave,
}: {
  draft: LessonDraft;
  setDraft: (draft: LessonDraft) => void;
  saving: boolean;
  onSave: () => void;
}) {
  return (
    <form className="preferences-editor" onSubmit={(event) => { event.preventDefault(); onSave(); }}>
      <div className="preferences-editor-grid">
        <label className="field">
          <span>Type</span>
          <select value={draft.kind} onChange={(event) => setDraft({ ...draft, kind: event.target.value as LessonKind })}>
            <option value="rule">Rule</option>
            <option value="preference">Contextual preference</option>
            <option value="example">Example</option>
          </select>
        </label>
        <label className="field">
          <span>Applies to</span>
          <select value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value as LessonCategory })}>
            <option value="writing">Writing</option><option value="selection">Selection</option><option value="method">Method</option>
          </select>
        </label>
        <label className="field">
          <span>Status</span>
          <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value as LessonStatus })}>
            <option value="active">Active</option><option value="inactive">Inactive</option><option value="conflict">Needs review</option>
          </select>
        </label>
      </div>
      <label className="field">
        <span>{draft.kind === 'rule' ? 'Rule' : draft.kind === 'preference' ? 'Preference' : 'Example'}</span>
        <textarea required rows={6} value={draft.text} onChange={(event) => setDraft({ ...draft, text: event.target.value })} />
      </label>
      <label className="field"><span>When it applies</span><textarea rows={3} value={draft.context} placeholder="Optional context or boundary" onChange={(event) => setDraft({ ...draft, context: event.target.value })} /></label>
      <Disclosure title="Source evidence" meta="Advanced" className="preferences-evidence-editor">
        <label className="field"><span>Evidence records</span><textarea className="mono" rows={7} value={draft.sourceEvidenceText} onChange={(event) => setDraft({ ...draft, sourceEvidenceText: event.target.value })} /><span className="hint">Stored evidence is kept intact. Edit the structured records only when necessary.</span></label>
      </Disclosure>
      <button className="primary" disabled={saving}>{saving ? 'Saving…' : 'Save preference'}</button>
    </form>
  );
}

export function PreferencesPanel({ profileId }: { profileId: number }) {
  const [lessons, setLessons] = useState<EditorialLesson[]>([]);
  const [view, setView] = useState<PreferenceView>('rule');
  const [status, setStatus] = useState<LessonStatus | ''>('active');
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [draft, setDraft] = useState<LessonDraft>(EMPTY_LESSON);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    const query = new URLSearchParams({ profileId: String(profileId) });
    if (status) query.set('status', status);
    const response = await api.get<{ items: EditorialLesson[] }>(`/editorial/lessons?${query}`);
    setLessons(response.items);
  }, [profileId, status]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err))).finally(() => setLoading(false));
  }, [load]);

  const counts = useMemo(() => ({
    rule: lessons.filter((lesson) => lesson.kind === 'rule').length,
    preference: lessons.filter((lesson) => lesson.kind === 'preference').length,
    example: lessons.filter((lesson) => lesson.kind === 'example').length,
  }), [lessons]);

  const visible = useMemo(() => lessons.filter((lesson) => lesson.kind === view), [lessons, view]);

  function openNew(kind: PreferenceView) {
    setError(null);
    setDraft({ ...EMPTY_LESSON, kind });
    setEditingId('new');
  }

  async function saveLesson() {
    setSaving(true); setError(null);
    try {
      const payload = { category: draft.category, kind: draft.kind, text: draft.text.trim(), context: draft.context.trim() || null, sourceEvidence: evidence(draft.sourceEvidenceText), status: draft.status };
      if (editingId === 'new') await api.post('/editorial/lessons', { profileId, ...payload });
      else if (typeof editingId === 'number') {
        const current = lessons.find((lesson) => lesson.id === editingId);
        if (!current) return;
        await api.patch(`/editorial/lessons/${editingId}`, { expectedVersion: current.version, ...payload });
      }
      await load(); setEditingId(null); setView(draft.kind); setNotice(editingId === 'new' ? 'Preference added.' : 'Preference updated.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setError('This preference changed elsewhere. Reload it before saving again.'); else setError(err instanceof Error ? err.message : String(err));
    } finally { setSaving(false); }
  }

  async function setLessonStatus(lesson: EditorialLesson, next: LessonStatus) {
    setSaving(true); setError(null);
    try {
      await api.patch(`/editorial/lessons/${lesson.id}`, { expectedVersion: lesson.version, status: next });
      await load(); setNotice(next === 'inactive' ? 'Preference deactivated.' : 'Preference activated.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setError('This preference changed elsewhere. Reload the page before trying again.'); else setError(err instanceof Error ? err.message : String(err));
    } finally { setSaving(false); }
  }

  async function restore(lesson: EditorialLesson, version: number) {
    setSaving(true); setError(null);
    try {
      await api.post(`/editorial/lessons/${lesson.id}/restore`, { expectedVersion: lesson.version, targetVersion: version });
      await load(); setNotice(`Restored version ${version}.`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setError('This preference changed elsewhere. Reload the page before restoring.'); else setError(err instanceof Error ? err.message : String(err));
    } finally { setSaving(false); }
  }

  return (
    <section className="preferences-panel">
      <div className="preferences-heading">
        <div><h2>Preferences</h2><p>Rules and context that shape how this content is selected and written.</p></div>
        <div className="preferences-heading-actions">
          <label><span className="sr-only">Status</span><select value={status} onChange={(event) => setStatus(event.target.value as LessonStatus | '')}><option value="active">Active</option><option value="">All statuses</option><option value="inactive">Inactive</option><option value="conflict">Needs review</option></select></label>
          <button className="primary" onClick={() => openNew(view)}>Add {view === 'rule' ? 'rule' : view === 'preference' ? 'preference' : 'example'}</button>
        </div>
      </div>

      {error ? <p className="editorial-alert fail" role="alert">{error}</p> : null}
      {notice ? <p className="editorial-alert" role="status">{notice}</p> : null}

      <nav className="preferences-tabs" aria-label="Preference types">
        {(Object.keys(VIEW_LABELS) as PreferenceView[]).map((item) => <button type="button" key={item} aria-pressed={view === item} onClick={() => setView(item)}><span>{VIEW_LABELS[item]}</span><small>{counts[item]}</small></button>)}
      </nav>

      {view === 'example' ? <p className="preferences-context-note">Examples are references, not rules. They remain separate so they do not silently become universal guidance.</p> : null}

      {loading ? <div className="preferences-empty">Loading preferences…</div> : visible.length === 0 ? (
        <div className="preferences-empty"><strong>No {VIEW_LABELS[view].toLowerCase()} in this view</strong><span>Add one when there is durable guidance worth keeping.</span></div>
      ) : (
        <div className="preferences-list">
          {visible.map((lesson) => (
            <article className={`preferences-row preferences-${lesson.status}`} key={lesson.id}>
              <div className="preferences-row-main">
                <div className="preferences-row-meta"><span>{lesson.category}</span><span>{lesson.status === 'conflict' ? 'Needs review' : lesson.status}</span><span>v{lesson.version}</span></div>
                <p>{lesson.text}</p>
                {lesson.context ? <p className="preferences-context">When: {lesson.context}</p> : null}
              </div>
              <div className="preferences-row-actions">
                <button disabled={saving} onClick={() => { setError(null); setEditingId(lesson.id); setDraft(toDraft(lesson)); }}>Edit</button>
                <button disabled={saving} onClick={() => void setLessonStatus(lesson, lesson.status === 'active' ? 'inactive' : 'active')}>{lesson.status === 'active' ? 'Deactivate' : 'Activate'}</button>
              </div>
              {lesson.sourceEvidence.length > 0 ? (
                <Disclosure title="Evidence" meta={`${lesson.sourceEvidence.length} ${lesson.sourceEvidence.length === 1 ? 'source' : 'sources'}`} className="preferences-row-disclosure">
                  <ol className="preferences-evidence-list">{lesson.sourceEvidence.map((item, index) => <li key={index}>{evidenceLabel(item, index)}</li>)}</ol>
                </Disclosure>
              ) : null}
              {lesson.history.length > 0 ? (
                <Disclosure title="History" meta={`${lesson.history.length} versions`} className="preferences-row-disclosure">
                  <ol className="preferences-history">
                    {lesson.history.map((version) => <li key={version.version}><div><strong>Version {version.version}</strong><span>{version.actor} · {formatDateTime(version.recordedAt)}</span></div><p>{version.text}</p>{version.version !== lesson.version ? <button disabled={saving} onClick={() => void restore(lesson, version.version)}>Restore this version</button> : null}</li>)}
                  </ol>
                </Disclosure>
              ) : null}
            </article>
          ))}
        </div>
      )}

      {editingId !== null ? (
        <Dialog title={editingId === 'new' ? `New ${draft.kind === 'preference' ? 'contextual preference' : draft.kind}` : 'Edit preference'} onClose={() => setEditingId(null)} footer={<button onClick={() => setEditingId(null)}>Cancel</button>}>
          {error ? <p className="editorial-alert fail" role="alert">{error}</p> : null}
          <LessonEditor draft={draft} setDraft={setDraft} saving={saving} onSave={() => void saveLesson()} />
        </Dialog>
      ) : null}
    </section>
  );
}

export default function Lessons() {
  const [searchParams] = useSearchParams();
  const profileId = searchParams.get('profileId') ?? searchParams.get('profile');
  const publicationId = searchParams.get('publicationId') ?? searchParams.get('publication');
  const next = new URLSearchParams({ tab: 'preferences' });
  if (profileId) next.set('profileId', profileId);
  if (publicationId) next.set('publicationId', publicationId);
  return <Navigate to={`/dashboard?${next.toString()}`} replace />;
}
